import { type FieldOption, isSome } from "@sveabank/cc-common";
import { formatPhone } from "@sveabank/cc-common";
import { useState } from "react";
import { useApplicationId } from "../../hooks/useApplicationId";
import { useCountries } from "../../hooks/useCountries";
import { useSteps } from "../../hooks/useSteps";
import {
	useConcludeContactInfoMutation,
	useFetchApplicationDetailsQuery,
} from "../../services/application";
import type { ContactFormValues } from "../../types/contact-information";
import { convertCountriesToFormValues, getInitialValuesFromStep } from "./helpers";

export const useContactForm = () => {
	const { isLoading: isStepsLoading, steps } = useSteps();
	const { isLoading: isCountriesLoading, sortedCountriesByCallingCode = [] } = useCountries();
	const applicationId = useApplicationId();

	const [registerAndSetupAgreements, { isLoading: isConcludeContactInfoLoading }] =
		useConcludeContactInfoMutation();

	const { data: applicationDetails, isLoading: isApplicationDetailsLoading } =
		useFetchApplicationDetailsQuery(applicationId);

	const [hasFormError, setHasFormError] = useState(false);

	const submitContactInfo = (values: ContactFormValues) => {
		const organisation = {
			email: values.companyEmail,
			phone: {
				countryCode: values.companyCountryCode,
				number: formatPhone(values.companyPhone),
			},
		};

		const applicant = {
			email: values.email,
			phone: {
				countryCode: values.countryCode,
				number: formatPhone(values.phone),
			},
		};

		return registerAndSetupAgreements({
			applicantContactInfo: applicant,
			organizationContactInfo: organisation,
			applicationId,
		}).unwrap();
	};

	const countriesGroupedByCallingCode = convertCountriesToFormValues(sortedCountriesByCallingCode);

	const countries: Array<FieldOption> = Array.from(
		countriesGroupedByCallingCode,
		([value, label]) => ({ label, value }),
	);

	const initialFormValues = getInitialValuesFromStep(steps);

	const isLoading = isSome(isCountriesLoading, isStepsLoading, isApplicationDetailsLoading);

	return {
		submitContactInfo,
		countries,
		initialFormValues,
		isConcludeContactInfoLoading,
		isLoading,
		hasFormError,
		setHasFormError,
		applicationDetails,
	};
};
